<template>
  <div class="modal-overlay" v-if="visible">
    <div class="modal">
      <div class="modal-header">
        <h3 class="modal-title">{{ title }}</h3>
        <button v-if="!loadingCountMessage" class="close-btn" @click="$emit('close')">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <!-- 이미지 -->
        <div v-if="previewImageUrl" class="image-container">
          <img :src="previewImageUrl" alt="미리보기 이미지"/>
        </div>

        <!-- 설명 -->
        <div class="description-container">
          <p class="description">{{ description }}</p>
        </div>

        <!-- 신청 폼 -->
        <div class="form-container">
          <FormKit
              type="form"
              :value="{}"
              @submit="submit"
              submit-label="제출"
              :submit-attrs="{ class: 'submit-btn' }"
              incomplete-message="필수 항목을 입력해주세요."
          >

            <template v-for="field in formFields" :key="field.name">
              <FileInput
                  v-if="field.type === 'file'"
                  :name="field.name"
                  :label="field.label"
                  :help="field.help"
                  :validation="field.validation"
                  :accept="field.accept"
                  :multiple="field.multiple"
                  :maxFileSize="field.maxFileSize"
              />

              <!-- '기타' 옵션이 있는 경우 (checkbox, radio 등) -->
              <template v-else-if="hasEtcOption(field)">
                <FormKit
                    :type="field.type"
                    :name="field.name"
                    :label="field.label"
                    :placeholder="field.placeholder"
                    :help="field.help"
                    :validation="field.validation"
                    :options="field.options"
                    v-model="formValues[field.name]"
                    :validation-messages="{required: `[${field.label}] 등록 필수`}"
                />
                <FormKit
                    v-if="formValues[field.name]?.includes?.('ETC') || formValues[field.name] === 'ETC'"
                    :name="`${field.name}_etc`"
                    type="text"
                    label="기타 항목 입력"
                    placeholder="기타 항목을 입력하세요"
                    validation="required"
                    :validation-messages="{required: `기타 항목을 입력해주세요.`}"
                />
              </template>

              <template v-else-if="field.name === 'mimio_portfolio'">
                <FormKit
                    type="hidden"
                    :name="field.name + '_with_path'"
                    :label="field.label"
                    v-model="formValues[field.name]"
                />
                <div style="position: relative">
                  <FormKit
                      :type="field.type"
                      :name="field.name"
                      :label="field.label"
                      :placeholder="field.placeholder"
                      :help="field.help"
                      :validation="field.validation"
                      :options="field.options || []"
                      :disabled="field.disabled"
                      :validation-messages="{ required: `[${field.label}] 등록 필수` }"
                      :suffix-icon="(downloading? 'refresh' : 'download')"
                      @suffix-icon-click="handleDownloadClick"
                      v-model="portfolioFileName"
                  />
                  <DownloadButtonPortfolioPdf ref="downloadBtnRef" class="download-button"/>
                </div>
                <FormKit
                    v-if="formValues[field.name]"
                    type="button"
                    prefix-icon="refresh"
                    @click="loadUserPortfolioOnly"
                    style="background-color: #151419; color: #EAEAEA; border: 1px solid #EAEAEA; border-radius: 4px;"
                >
                  최신 포트폴리오 다시 불러오기
                </FormKit>
                <template v-else>
                  <FormKit
                      v-if="!isPortfolioCreated"
                      type="button"
                      @click="goToPortfolioCreate"
                      style="background-color: #FFF3CE; color: #151419; border-radius: 4px;"
                  >
                    <template #prefix>
                    <span class="formkit-prefix-icon formkit-icon">
                      <img src="@/assets/images/icon/ico_edit_b.svg" alt=""/>
                    </span>
                    </template>
                    포트폴리오 제작 바로가기
                  </FormKit>

                  <FormKit
                      v-else
                      type="button"
                      prefix-icon="refresh"
                      @click="loadUserPortfolioOnly"
                      style="background-color: #151419; color: #EAEAEA; border: 1px solid #EAEAEA; border-radius: 4px;"
                  >
                    포트폴리오 불러오기
                  </FormKit>
                </template>
              </template>

              <template v-else-if="field.name.startsWith('mimio_')">
                <FormKit
                    :type="field.type"
                    :name="field.name"
                    :label="field.label"
                    :placeholder="field.placeholder"
                    :help="field.help"
                    :validation="field.validation"
                    :options="field.options || []"
                    :disabled="field.disabled"
                    :validation-messages="{ required: `[${field.label}] 등록 필수` }"
                    v-model="formValues[field.name]"
                />
              </template>

              <FormKit
                  v-else
                  :type="field.type"
                  :name="field.name"
                  :label="field.label"
                  :placeholder="field.placeholder"
                  :help="field.help"
                  :validation="field.validation"
                  :options="field.options || []"
                  :validation-messages="{required: `[${field.label}] 등록 필수`}"
              />
            </template>

          </FormKit>
          <span v-if="loadingCountMessage" style="font-size: 13px; color: rgba(0 0 0 / 60%);">{{ loadingCountMessage }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, defineProps, watch, reactive, onMounted, computed} from 'vue';
import {apiClient} from '@/services/auth-header';
import FileInput from "@/views/contest/FileInput.vue";
import {useSlack} from '@/composables/useSlack';
import {useAnalytics} from '@/composables/useAnalytics';
import DownloadButtonPortfolioPdf from "@/components/ui/DownloadButtonPortfolioPdf.vue";
import {useRoute} from "vue-router";

const route = useRoute();
const isInternal = route.name === 'InternalNewsDetail';

const {sendErrorMessage} = useSlack();
const {trackFormSubmit} = useAnalytics();

const props = defineProps({
  visible: Boolean,
  contestUuid: String,
  formFields: Array,
  previewImageUrl: String,
  title: String,
  description: String
})

const emit = defineEmits(['close'])

// 기타 텍스트 입력 조건용 반응형 모델
const formValues = reactive({})

const portfolioFileName = computed(() => {
  const fullPath = formValues['mimio_portfolio']
  if (!fullPath) {
    return ''
  }
  return fullPath.split('/').pop()
})

const downloading = ref(false);
const downloadBtnRef = ref(null);

const handleDownloadClick = async () => {
  if (!portfolioFileName.value) {
    return;
  }

  if (downloading.value) {
    return;
  }

  downloading.value = true
  if (downloadBtnRef.value[0]) {
    await downloadBtnRef.value[0].downloadFiles();
  }
  downloading.value = false;
};

const isPortfolioCreated = ref(false)
const goToPortfolioCreate = () => {
  window.open('/my/artist/portfolio', '_blank')
  isPortfolioCreated.value = true
}

const hasEtcOption = (field) => {
  return Array.isArray(field.options) &&
      field.options.some(opt => opt.value === 'ETC')
}

const userInfo = ref({})

watch(
    () => props.visible,
    (visible) => {
      if (visible && !isInternal) {
        loadUserInfo()
      }
    }
)

const loadUserInfo = async () => {
  try {
    const {data} = await apiClient.get('/v1/artists/contest-applicants/prefill')
    userInfo.value = data

    // 사용자 정보 기반으로 formValues 초기 세팅
    for (const [key, value] of Object.entries(data)) {
      formValues[key] = value ?? ''
    }
  } catch (e) {
    console.error('사용자 정보 불러오기 실패:', e)
    alert('사용자 정보를 불러오지 못했습니다.')
  }
}

const loadUserPortfolioOnly = async () => {
  try {
    const {data} = await apiClient.get('/v1/artists/contest-applicants/portfolio');
    if (data) {
      formValues['mimio_portfolio'] = data;
    } else {
      alert('포트폴리오가 없습니다.');
    }
  } catch (e) {
    console.error('포트폴리오 조회 실패:', e);
  }
};

const loadingMessage = '첨부 파일 용량에 따라 시간이 다소 소요될 수 있습니다.';
const loadingCountMessage = ref('');

const submit = async (formData) => {
  if (isInternal) {
    alert("미리보기 화면입니다. 신청이 불가.");
    return;
  }

  loadingCountMessage.value = loadingMessage;
  let currentStep = '';
  let s3Prefix = '';

  try {
    // ✅ Step 0: 공모전 활성 상태 확인
    currentStep = '공모전 활성 상태 확인 중';
    const { data: isActive } = await apiClient.get(`/v1/contents/contests/${props.contestUuid}/active`);
    if (!isActive) {
      alert("이 공모전은 종료되었거나 비활성화되었습니다.");
      loadingCountMessage.value = '';
      return;
    }

    // ✅ Step 1: 폼 데이터 저장
    currentStep = '폼 데이터 저장 중';
    s3Prefix = await saveFormData(formData);

    // ✅ Step 2: 업로드 대상 추출
    currentStep = 'S3 업로드 대상 추출 중';
    const uploadTargets = getUploadTargets(formData, s3Prefix);

    // ✅ Step 3: S3 업로드
    currentStep = '파일 S3 업로드 중';
    await uploadFilesToS3(uploadTargets, 5);

    // ✅ Step 4: 업로드 완료 처리
    currentStep = '업로드 완료 처리 중';
    await markAsUploaded(s3Prefix);

    // 전환 추적 (mimio#41) — 성공 시에만 발화한다. GA4 주요 이벤트는 파라미터와 무관하게
    // 이벤트명 발생 횟수를 세므로, 실패까지 보내면 전환 수가 부풀려진다.
    trackFormSubmit('contest', props.contestUuid || '', true);

    alert('공모전 신청이 완료되었습니다.\n이메일에서 신청정보를 확인하세요(1~2분 소요).');
    emit('close');

    Object.keys(formValues).forEach(key => delete formValues[key]);
    loadingCountMessage.value = '';
  } catch (e) {
    const message = e.message?.startsWith?.('❌')
        ? e.message
        : `❌ 공모전 신청 실패 - ${currentStep}\n - contestUuid: ${props.contestUuid} \n - s3Prefix: ${s3Prefix}`;

    await sendErrorMessage(message);

    alert('공모전 신청 중 문제가 발생했습니다.\n잠시 후 다시 시도하시거나 관리자에게 문의해 주세요.');
    location.reload();
  }
};

const saveFormData = async (formData) => {

  const {data} = await apiClient.post(`/v1/artists/contest-applicants/${props.contestUuid}`, {
    formData: JSON.stringify(formData)
  })

  return data;
}

const getUploadTargets = (formData, s3Prefix) => {
  const uploadTargets = [];

  Object.entries(formData).forEach(([key, value]) => {
    if (!key.startsWith('file_') || !Array.isArray(value)) {
      return;
    }

    value.forEach((entry) => {
      const file = entry.file;
      if (!(file instanceof File)) {
        return;
      }

      const fileName = entry.name || file.name;
      const s3Key = `${s3Prefix}${key}/${fileName}`;
      const contentType = file.type || 'application/octet-stream';

      uploadTargets.push({file, s3Key, contentType});
    });
  });

  return uploadTargets;
};

const uploadFilesToS3 = async (uploadTargets, concurrency = 3) => {
  let currentIndex = 0;
  let completedCount = 0;
  const total = uploadTargets.length;

  async function uploadFile({file, s3Key, contentType}) {
    const res = await apiClient.post('/v1/s3/put/presigned-url', {
      fileName: s3Key,
      contentType
    });

    try {
      const uploadRes = await fetch(res.data, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType
        },
        body: file
      });

      if (!uploadRes.ok) {
        throw new Error();
      }

      completedCount++;
      // uiStore.showLoading(loadingMessage + `(${completedCount}/${total})`);
      loadingCountMessage.value = loadingMessage + ` (${completedCount}/${total})`;

    } catch (e) {
      const message = `❌ 공모전 신청 S3 업로드 실패.\n - s3Key: ${s3Key}`;
      throw new Error(message);
    }
  }

  async function processQueue() {
    const workers = [];

    for (let i = 0; i < concurrency; i++) {
      const worker = (async () => {
        while (currentIndex < total) {
          const index = currentIndex++;
          const target = uploadTargets[index];
          if (target) {
            await uploadFile(target);
          }
        }
      })();
      workers.push(worker);
    }

    await Promise.all(workers);
  }

  await processQueue();
};

const markAsUploaded = async (s3Prefix) => {
  const segments = s3Prefix.split('/');
  const id = segments[segments.length - 2]; // "지원서 ID"가 마지막에서 두 번째

  await apiClient.put(`/v1/artists/contest-applicants/${id}/uploaded`);
};

</script>

<style scoped>
.download-button {
  position: absolute;
  top: 25px;
  right: 2px;
  z-index: -1;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: 20px;
}

.modal-overlay .modal {
  background: white;
  width: 800px;
  max-width: 100%;
  max-height: 85vh;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-overlay .modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f1f5f9;
}

.modal-overlay .modal .modal-header .modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.4;
}

.modal-overlay .modal .modal-header .close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: #f8fafc;
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.modal-overlay .modal .modal-header .close-btn:hover {
  background: #f1f5f9;
  color: #334155;
}

.modal-overlay .modal .modal-body {
  padding: 0 24px 24px 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-overlay .modal .modal-body .image-container {
  margin-bottom: 20px;
}

.modal-overlay .modal .modal-body .image-container img {
  width: 100%;
  height: auto;
  max-height: 200px;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.modal-overlay .modal .modal-body .description-container {
  margin-bottom: 24px;
}

.modal-overlay .modal .modal-body .description-container .description {
  font-size: 16px;
  line-height: 1.6;
  color: #1e293b;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: keep-all;
}

.modal-overlay .modal .modal-body .form-container :deep(.formkit-wrapper) {
  max-width: 100%;
  width: 100%;
}

@media (max-width: 640px) {
  .modal-overlay {
    padding: 16px;
  }

  .modal-overlay .modal {
    max-width: 100%;
    max-height: 90vh;
    border-radius: 12px;
  }

  .modal-overlay .modal .modal-header {
    padding: 20px 20px 16px 20px;
  }

  .modal-overlay .modal .modal-header .modal-title {
    font-size: 16px;
  }

  .modal-overlay .modal .modal-body {
    padding: 0 20px 20px 20px;
  }

  .modal-overlay .modal .modal-header .close-btn {
    width: 28px;
    height: 28px;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 12px;
  }

  .modal-overlay .modal .modal-header {
    padding: 16px 16px 12px 16px;
  }

  .modal-overlay .modal .modal-body {
    padding: 0 16px 16px 16px;
  }

  .modal-overlay .modal .modal-body .description-container .description {
    font-size: 13px;
  }
}

.modal-body::-webkit-scrollbar {
  width: 8px; /* 스크롤바 너비 */
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.8); /* 스크롤 핸들 색 */
  border-radius: 4px; /* 둥근 모서리 */
}

.modal-body::-webkit-scrollbar-track {
  background-color: transparent; /* 스크롤 트랙 배경 */
}

</style>